import { Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import Home from './pages/Home';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Stats from './pages/Stats';
import ListYourParking from './pages/ListYourParking';
import Seekers from './pages/Seekers';
import PinYourRent from './pages/PinYourRent';
import Contribute from './pages/Contribute';
import CityPage from './pages/CityPage';
import LocalityPage from './pages/LocalityPage';
import Prices from './pages/Prices';
import CityPricePage from './pages/CityPricePage';
import LocalityPricePage from './pages/LocalityPricePage';
import CheapestParking from './pages/CheapestParking';
import MostExpensiveParking from './pages/MostExpensiveParking';
import EVChargingParking from './pages/use-cases/EVChargingParking';
import TwoWheelerMonthlyParking from './pages/use-cases/TwoWheelerMonthlyParking';
import AirportParking from './pages/use-cases/AirportParking';
import MonthlyCarParking from './pages/use-cases/MonthlyCarParking';
import BikeParkingNearMetro from './pages/use-cases/BikeParkingNearMetro';
import CommercialParkingOperator from './pages/use-cases/CommercialParkingOperator';
import ApartmentVisitorParking from './pages/use-cases/ApartmentVisitorParking';
import SeekerParkingGuide from './pages/use-cases/SeekerParkingGuide';
import TowingCostIndia from './pages/mission/TowingCostIndia';
import ParkingLawsIndia from './pages/mission/ParkingLawsIndia';
import TrafficChaosIndia from './pages/mission/TrafficChaosIndia';
import EVParkingIndia from './pages/mission/EVParkingIndia';
import TwoWheelerParkingIndia from './pages/mission/TwoWheelerParkingIndia';
import WomenSafetyParkingIndia from './pages/mission/WomenSafetyParkingIndia';
import ParkingCarbonFootprint from './pages/mission/ParkingCarbonFootprint';
import ParkingVsTollIndia from './pages/mission/ParkingVsTollIndia';
import Guides from './pages/Guides';
import IndiaParkingReport from './pages/IndiaParkingReport';
import Comparison from './pages/Comparison';
import Guide from './pages/Guide';
import { I18nProvider, isLocale, type Locale } from './i18n';
import { detectLocaleFromPath } from './i18n';
import LanguageSwitcher from './components/LanguageSwitcher';

function AppHeader() {
  return (
    <header className="bg-navy-900 text-white shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-bold text-xl">
          <span className="text-parksy-500">🅿️</span>
          <span>parksy</span>
          <span className="text-parksy-500">.rent</span>
        </Link>
        <div className="flex items-center gap-3">
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            <Link to="/" className="hover:text-parksy-500 transition">Map</Link>
            <Link to="/stats" className="hover:text-parksy-500 transition">Stats</Link>
            <Link to="/list-your-parking" className="hover:text-parksy-500 transition">List</Link>
            <Link to="/about" className="hover:text-parksy-500 transition">About</Link>
            <Link to="/faq" className="hover:text-parksy-500 transition">FAQ</Link>
          </nav>
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function AppFooter() {
  return (
    <footer className="bg-white border-t py-6">
      <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-600">
        <p className="mb-2">
          <span className="font-semibold">parksy.rent</span> — India's crowdsourced parking map.
        </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/about" className="hover:text-parksy-600">About</Link>
            <Link to="/faq" className="hover:text-parksy-600">FAQ</Link>
            <Link to="/stats" className="hover:text-parksy-600">Stats</Link>
            <Link to="/list-your-parking" className="hover:text-parksy-600">List parking</Link>
            <Link to="/privacy" className="hover:text-parksy-600">Privacy</Link>
            <Link to="/terms" className="hover:text-parksy-600">Terms</Link>
            <Link to="/contact" className="hover:text-parksy-600">Contact</Link>
          </div>
        <p className="mt-4 text-xs text-gray-400">
          © {new Date().getFullYear()} Parksy.rent. Free forever. No brokerage.
        </p>
      </div>
    </footer>
  );
}

function LocaleWrapper({ locale, children }: { locale: Locale; children: ReactNode }) {
  return <I18nProvider locale={locale}>{children}</I18nProvider>;
}

function MarketingRoutes() {
  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1">
        <Routes>
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/list-your-parking" element={<ListYourParking />} />
          <Route path="/seekers" element={<Seekers />} />
          <Route path="/pin-your-rent" element={<PinYourRent />} />
          <Route path="/contribute" element={<Contribute />} />
          <Route path="/prices" element={<Prices />} />
          <Route path="/cheapest-parking" element={<CheapestParking />} />
          <Route path="/most-expensive-parking" element={<MostExpensiveParking />} />
          <Route path="/ev-charging-parking" element={<EVChargingParking />} />
          <Route path="/two-wheeler-monthly-parking" element={<TwoWheelerMonthlyParking />} />
          <Route path="/airport-parking" element={<AirportParking />} />
          <Route path="/monthly-car-parking" element={<MonthlyCarParking />} />
          <Route path="/bike-parking-near-metro" element={<BikeParkingNearMetro />} />
          <Route path="/commercial-parking-operator" element={<CommercialParkingOperator />} />
          <Route path="/apartment-visitor-parking" element={<ApartmentVisitorParking />} />
          <Route path="/seeker-parking-guide" element={<SeekerParkingGuide />} />
          <Route path="/towing-cost-india" element={<TowingCostIndia />} />
          <Route path="/parking-laws-india" element={<ParkingLawsIndia />} />
          <Route path="/traffic-chaos-india" element={<TrafficChaosIndia />} />
          <Route path="/ev-parking-india" element={<EVParkingIndia />} />
          <Route path="/two-wheeler-parking-india" element={<TwoWheelerParkingIndia />} />
          <Route path="/women-safety-parking-india" element={<WomenSafetyParkingIndia />} />
          <Route path="/parking-carbon-footprint" element={<ParkingCarbonFootprint />} />
          <Route path="/parking-vs-toll-india" element={<ParkingVsTollIndia />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/:slug" element={<Guide />} />
          <Route path="/india-parking-report" element={<IndiaParkingReport />} />
          <Route path="/vs/:slug" element={<Comparison />} />
          <Route path="/prices/:city/:locality" element={<LocalityPricePage />} />
          <Route path="/prices/:city" element={<CityPricePage />} />
          <Route path="/:city/:locality" element={<LocalityPage />} />
          <Route path="/:slug" element={<CityPage />} />
        </Routes>
      </main>
      <AppFooter />
    </div>
  );
}

function App() {
  const { pathname } = useLocation();
  const { locale, rest } = detectLocaleFromPath(pathname);
  const isFullScreen = rest === '/' || rest.startsWith('/p/');

  return (
    <Routes>
      <Route path="/:lang(en|hi|ta|te|bn)/*" element={<LocaleRoute />} />
      <Route path="*" element={
        isFullScreen ? (
          <LocaleWrapper locale={locale}>
            <LocalesRoutes />
          </LocaleWrapper>
        ) : (
          <LocaleWrapper locale={locale}>
            <MarketingRoutes />
          </LocaleWrapper>
        )
      } />
    </Routes>
  );
}

function LocalesRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/p/:type/:id" element={<Home />} />
    </Routes>
  );
}

function LocaleRoute() {
  const { lang, '*': subPath } = useParams<{ lang: string; '*': string }>();
  if (!lang || !isLocale(lang)) {
    return <MarketingRoutes />;
  }
  const innerPath = '/' + (subPath || '');
  return (
    <LocaleWrapper locale={lang}>
      <Routes location={innerPath}>
        <Route path="/" element={<Home />} />
        <Route path="/p/:type/:id" element={<Home />} />
        <Route path="*" element={<MarketingRoutes />} />
      </Routes>
    </LocaleWrapper>
  );
}

export default App;
